import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useContext,
} from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { getStageColor } from "../constants/stageColors";
import Button from "../Button/Button";
import "./StageFilterPopup.css";
import trackedNotSelectedIcon from "../../Assets/Images/tracked-not-selected-icon.svg";
import trackedSelectedIcon from "../../Assets/Images/tracked-selected-icon.svg";
import createdDarkIcon from "../../Assets/Images/Created-Tracked-Candidates-A-Dark.svg";
import createdNotSelectDark from "../../Assets/Images/Created-Tracked-Candidates-NA-Dark.svg";
import DateFilter from "../DateFilter/DateFilter";
import StagePopupIcon from "../../Assets/Images/stagepop-close-icon.svg";
import CallStageIcon from "../../Assets/Images/can-call-stage.svg";
import TimeIcon from "../../Assets/Images/can-time.svg";
import CallStageDarkIcon from "../../Assets/Images/can-call-stage-dark.svg";
import TimeDarkIcon from "../../Assets/Images/can-date-dark.svg";
import { DynamicContext } from "../../Context/DynamicContext";
import DynamicListUsers from "../DynamicListUsers/DynamicListUsers";
import { useFormik } from "formik";
import candidateIcon from "../../Assets/Images/user-icon-filters-light.svg";
import candidateDarkIcon from "../../Assets/Images/user-icon-filters-dark.svg";
import industryIcon from "../../Assets/Images/industry-icon-filters-light.svg";
import industryDarkIcon from "../../Assets/Images/industry-icon-filters-dark.svg";
import { CustomDatePickerModal } from "../Date-Picker/DatePicker";
import calendarIcon from "../../Assets/Images/calendarIcon.png";
import darkCalendarIcon from "../../Assets/Images/calendar-dark-Icon.png";
import DynamicSelectInput from "../Select-Input/SelectInput";
import OptionsData from "../../Assets/Json/StaticOptions.json";

const { StaticOptions = {} } = OptionsData || {};
const { industry: DEFAULT_INDUSTRY_OPTIONS = [] } = StaticOptions || {};

const formatDateRangeButtonLabel = (date) => {
  if (!date) return "";

  return new Date(date)
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(",", "");
};

const StageFilterPopup = ({
  isOpen,
  initialFilters,
  initialSelected,
  stages = [],
  onApply,
  onApplyFull,
  onClose,
  setIsAllCandidatesActive = () => {},
  isTrackedByMeActive = false,
  setIsTrackedByMeActive = () => {},
  isCreatedByMeActive = false,
  setIsCreatedByMeActive = () => {},
  setTablePage = () => {},
  setSelectedCandidates = () => {},
  setIsSelectMode = () => {},
  setTrackedViewTab = () => {},
  isInitialLoading = false,
  isLoadingMore = false,
  isDarkMode = false,
  subtitle = "Create filters to narrow candidates",
  showCandidateViewSelection = true,
  showDateFilter = true,
  includeUserFilterWithoutMode = false,
  defaultCandidateViewOnOpen = null,
  showDateRangeFilter = false,
  fromDate = null,
  toDate = null,
  popupClassName = "",
  popupStyle = undefined,
  customContent = null,
  customActions = null,
  isCallStageActive = true,
  isIndustryActive = false,
  exportCandidateByFilter = () => {},
  setExportFilteredCandidatesUrl = () => {},
  isUsersFilterActive = true,
  showClearButton = true,
  onClear,
  callOnApplyFullDuringClear = true,
  industryOptions = DEFAULT_INDUSTRY_OPTIONS,
}) => {
  const formik = useFormik({
    initialValues: {
      users: "",
      industry: "",
    },
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const popupRef = useRef(null);
  const stageDropdownAnchorRef = useRef(null);
  const stageDropdownMenuRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const { userDetails, AdminAccess } = useContext(DynamicContext);
  const currentUserId = userDetails?.id || "";

  const normalizedInitialFilters = useMemo(() => {
    if (initialFilters && initialFilters.length > 0) {
      return initialFilters;
    }
    if (
      initialSelected &&
      Array.isArray(initialSelected) &&
      initialSelected.length > 0
    ) {
      return initialSelected.map((stage) => ({
        type: "call_stage",
        value: stage,
      }));
    }
    return [];
  }, [initialFilters, initialSelected]);
  const [tempStageValue, setTempStageValue] = useState("");
  const [tempDateValue, setTempDateValue] = useState("");
  const [tempFromDate, setTempFromDate] = useState(null);
  const [tempToDate, setTempToDate] = useState(null);
  const [tempIsTrackedByMeActive, setTempIsTrackedByMeActive] = useState(false);
  const [tempIsCreatedByMeActive, setTempIsCreatedByMeActive] = useState(false);
  const [openRangePicker, setOpenRangePicker] = useState(null);
  const [rangePickerPosition, setRangePickerPosition] = useState({
    top: null,
    left: null,
  });
  const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [stageDropdownRect, setStageDropdownRect] = useState(null);
  useEffect(() => {
    if (!isOpen) return;
    const stageFilter = normalizedInitialFilters.find(
      (f) => f.type === "call_stage",
    );
    const dateFilter = normalizedInitialFilters.find((f) => f.type === "date");
    const userFilter = normalizedInitialFilters.find(
      (f) => f.type === "user_id",
    );
    const industryFilter = normalizedInitialFilters.find(
      (f) => f.type === "industry",
    );
    setTempStageValue(stageFilter?.value || "");
    setTempDateValue(dateFilter?.value || "");
    setTempFromDate(fromDate || null);
    setTempToDate(toDate || null);
    const initialUserId = userFilter?.value || currentUserId || "";
    formik.setFieldValue("users", initialUserId);
    setSelectedUser(
      initialUserId
        ? {
            value: initialUserId,
            label:
              String(initialUserId) === String(currentUserId)
                ? "Me"
                : userFilter?.label || String(initialUserId),
          }
        : null,
    );
    formik.setFieldValue("industry", industryFilter?.value || "");
    const shouldDefaultToTrackedByMe =
      showCandidateViewSelection &&
      defaultCandidateViewOnOpen === "trackedByMe" &&
      !isTrackedByMeActive &&
      !isCreatedByMeActive;
    setTempIsTrackedByMeActive(
      shouldDefaultToTrackedByMe || isTrackedByMeActive,
    );
    setTempIsCreatedByMeActive(
      shouldDefaultToTrackedByMe ? false : isCreatedByMeActive,
    );
    setIsStageDropdownOpen(false);
    setIsDateDropdownOpen(false);
    setOpenRangePicker(null);
    setRangePickerPosition({ top: null, left: null });
    setStageDropdownRect(null);
  }, [
    normalizedInitialFilters,
    isOpen,
    isTrackedByMeActive,
    isCreatedByMeActive,
    currentUserId,
    userDetails?.role,
    fromDate,
    toDate,
    showCandidateViewSelection,
    defaultCandidateViewOnOpen,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key !== "Escape") return;

      if (isStageDropdownOpen) {
        setIsStageDropdownOpen(false);
      } else if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      } else if (typeof onClose === "function") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isStageDropdownOpen, isDateDropdownOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      const popup = popupRef.current;
      const stageMenu = stageDropdownMenuRef.current;

      const isInsidePopup = popup && popup.contains(event.target);
      const isInsideStageMenu = stageMenu && stageMenu.contains(event.target);
      const isInsideMuiPortal =
        event.target.closest?.(".MuiAutocomplete-popper") ||
        event.target.closest?.(".MuiPopover-root") ||
        event.target.closest?.(".MuiAutocomplete-listbox") ||
        event.target.closest?.(".date-picker-modal");

      if (isInsidePopup || isInsideStageMenu || isInsideMuiPortal) {
        return;
      }

      if (isDateDropdownOpen || openRangePicker) return;
      onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isDateDropdownOpen, openRangePicker, onClose]);

  useEffect(() => {
    if (!isStageDropdownOpen) return;
    const handleClickOutside = (e) => {
      const anchor = stageDropdownAnchorRef.current;
      const menu = stageDropdownMenuRef.current;
      if (
        (anchor && anchor.contains(e.target)) ||
        (menu && menu.contains(e.target))
      ) {
        return;
      }
      setIsStageDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isStageDropdownOpen]);

  const updateStageDropdownRect = () => {
    const anchor = stageDropdownAnchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    setStageDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  };

  const toggleStageDropdown = () => {
    setIsStageDropdownOpen((prev) => {
      const next = !prev;
      if (next) {
        updateStageDropdownRect();
        setIsDateDropdownOpen(false);
      } else {
        setStageDropdownRect(null);
      }
      return next;
    });
  };

  const handleStageSelect = (stage) => {
    setTempStageValue(stage);
    setIsStageDropdownOpen(false);
    setStageDropdownRect(null);
  };

  useEffect(() => {
    if (!isStageDropdownOpen) return;
    const handleScroll = () => updateStageDropdownRect();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isStageDropdownOpen]);

  const toggleDateDropdown = () => {
    setIsDateDropdownOpen((prev) => {
      const next = !prev;
      if (next) {
        setIsStageDropdownOpen(false);
        setStageDropdownRect(null);
      }
      return next;
    });
  };

  const handleDateSelect = (dateOption) => {
    setTempDateValue(dateOption);
    setIsDateDropdownOpen(false);
  };

  const openDateRangePicker = (pickerType) => {
    const targetRef = pickerType === "from" ? fromDateRef : toDateRef;
    const rect = targetRef.current?.getBoundingClientRect();

    if (!rect) return;

    setRangePickerPosition({
      top: rect.bottom + 5,
      left: rect.left,
    });
    requestAnimationFrame(() => setOpenRangePicker(pickerType));
  };

  const handleTrackedByMeClick = useCallback(() => {
    setTempIsTrackedByMeActive(true);
    setTempIsCreatedByMeActive(false);
    if (!formik.values.users && currentUserId) {
      formik.setFieldValue("users", currentUserId);
    }
  }, [formik, formik.values.users, currentUserId]);

  const handleCreatedByMeClick = useCallback(() => {
    setTempIsTrackedByMeActive(false);
    setTempIsCreatedByMeActive(true);
    if (!formik.values.users && currentUserId) {
      formik.setFieldValue("users", currentUserId);
    }
  }, [formik, formik.values.users, currentUserId]);

  const handleApply = () => {
    if (showCandidateViewSelection) {
      if (tempIsTrackedByMeActive) {
        setIsAllCandidatesActive(false);
        setIsTrackedByMeActive(true);
        setIsCreatedByMeActive(false);
      } else if (tempIsCreatedByMeActive) {
        setIsAllCandidatesActive(false);
        setIsTrackedByMeActive(false);
        setIsCreatedByMeActive(true);
      } else {
        setIsAllCandidatesActive(true);
        setIsTrackedByMeActive(false);
        setIsCreatedByMeActive(false);
      }
      setTablePage(1);
      setSelectedCandidates([]);
      setIsSelectMode(false);
      setTrackedViewTab(0);
    }
    const filtersWithValues = [];

    if (tempStageValue && tempStageValue.trim()) {
      filtersWithValues.push({
        type: "call_stage",
        value: tempStageValue.trim(),
      });
    }
    if (showDateFilter && tempDateValue && tempDateValue.trim()) {
      filtersWithValues.push({
        type: "date",
        value: tempDateValue.trim(),
      });
    }
    if (showDateRangeFilter) {
      if (tempFromDate) {
        filtersWithValues.push({
          type: "from_date",
          value: tempFromDate,
        });
      }
      if (tempToDate) {
        filtersWithValues.push({
          type: "to_date",
          value: tempToDate,
        });
      }
    }
    const industryValue = formik.values.industry?.trim();
    if (isIndustryActive && industryValue) {
      filtersWithValues.push({
        type: "industry",
        value: industryValue,
      });
    }
    const selectedUserId = formik.values.users || currentUserId || "";
    const shouldIncludeUser =
      selectedUserId &&
      (includeUserFilterWithoutMode ||
        tempIsCreatedByMeActive ||
        tempIsTrackedByMeActive);
    if (shouldIncludeUser) {
      if (!formik.values.users) {
        formik.setFieldValue("users", selectedUserId);
      }
      const existingFilter = normalizedInitialFilters.find(
        (f) =>
          f.type === "user_id" && String(f.value) === String(selectedUserId),
      );
      const selectedUserOption =
        selectedUser &&
        String(selectedUser.value) === String(selectedUserId)
          ? selectedUser
          : null;

      filtersWithValues.push({
        type: "user_id",
        value: selectedUserId,
        label: selectedUserOption
          ? selectedUserOption.label
          : String(selectedUserId) === String(currentUserId)
            ? "Me"
            : existingFilter?.label || selectedUserId,
      });
    }

    if (typeof onApply === "function" && !onApplyFull) {
      onApply(tempStageValue ? [tempStageValue] : []);
    }
    if (typeof onApplyFull === "function") {
      const shouldClose = onApplyFull(filtersWithValues);
      if (shouldClose === false) {
        return;
      }
    }
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof exportCandidateByFilter === "function") {
      exportCandidateByFilter(
        filtersWithValues,
        tempIsTrackedByMeActive,
        tempIsCreatedByMeActive,
      );
    }
  };

  const handleClear = () => {
    setTempStageValue("");
    setTempDateValue("");
    setTempFromDate(null);
    setTempToDate(null);
    setOpenRangePicker(null);
    setRangePickerPosition({ top: null, left: null });
    setTempIsTrackedByMeActive(false);
    setTempIsCreatedByMeActive(false);
    setSelectedUser(
      currentUserId
        ? { value: currentUserId, label: "Me" }
        : null,
    );
    formik.setFieldValue("users", currentUserId || "");
    formik.setFieldValue("industry", "");
    if (showCandidateViewSelection) {
      setIsAllCandidatesActive(true);
      setIsTrackedByMeActive(false);
      setIsCreatedByMeActive(false);
      setTablePage(1);
      setSelectedCandidates([]);
      setIsSelectMode(false);
      setTrackedViewTab(0);
    }
    if (typeof onApplyFull === "function" && callOnApplyFullDuringClear) {
      onApplyFull([]);
    }
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof setExportFilteredCandidatesUrl === "function") {
      setExportFilteredCandidatesUrl("");
    }
    onClear?.();
  };

  if (!isOpen) return null;

  const popupContent = (
    <>
      <div
        className={`stage-popup ${popupClassName}`.trim()}
        style={popupStyle}
        ref={popupRef}
        role="dialog"
        aria-labelledby="stage-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stage-popup-header">
          <div>
            <h3 id="stage-popup-title" className="stage-popup-title">
              Filter
            </h3>
            <div className="stage-popup-subtitle">{subtitle}</div>
          </div>
          <button
            className="stage-popup-close"
            onClick={() => {
              if (typeof onClose === "function") onClose();
            }}
            aria-label="Close filter popup"
          >
            <img src={StagePopupIcon} alt="Close" />
          </button>
        </div>
        {customContent ? (
          <>
            <div className="stage-popup-body">{customContent}</div>
            {customActions && (
              <div className="stage-popup-actions">{customActions}</div>
            )}
          </>
        ) : (
          <>
            {showCandidateViewSelection && (
              <div className="candidate-view-selection">
                <Button
                  label="Tracked By"
                  iconSrc={
                    tempIsTrackedByMeActive
                      ? isDarkMode
                        ? createdDarkIcon
                        : trackedSelectedIcon
                      : isDarkMode
                        ? createdNotSelectDark
                        : trackedNotSelectedIcon
                  }
                  iconPosition="left"
                  iconStyle={{ marginRight: "8px" }}
                  event={handleTrackedByMeClick}
                  disabled={
                    initialFilters.length === 0 &&
                    (isInitialLoading || isLoadingMore)
                  }
                  className={`track-can-btn ${tempIsTrackedByMeActive ? "active" : ""}`}
                />
                <Button
                  label="Created By"
                  iconSrc={
                    tempIsCreatedByMeActive
                      ? isDarkMode
                        ? createdDarkIcon
                        : trackedSelectedIcon
                      : isDarkMode
                        ? createdNotSelectDark
                        : trackedNotSelectedIcon
                  }
                  iconPosition="left"
                  iconStyle={{ marginRight: "8px" }}
                  event={handleCreatedByMeClick}
                  disabled={
                    initialFilters.length === 0 &&
                    (isInitialLoading || isLoadingMore)
                  }
                  className={`track-can-btn ${tempIsCreatedByMeActive ? "active" : ""}`}
                />
              </div>
            )}
            <div className="stage-popup-body">
              {AdminAccess && isUsersFilterActive && (
                <div className="stage-filter-row">
                  <div className="stage-filter-controls">
                    <div className="filter-label">
                      <img
                        src={isDarkMode ? candidateDarkIcon : candidateIcon}
                        alt="Users"
                        className="filter-label-icon"
                      />
                      <span>Users</span>
                    </div>
                    <div className="stage-filter-value-wrapper">
                      <div style={{ width: "100%" }}>
                        <DynamicListUsers
                          value={selectedUser}
                          onChange={(value) => {
                            const selected = value
                              ? {
                                  ...value,
                                  label:
                                    String(value.value) === String(currentUserId)
                                      ? "Me"
                                      : value.label,
                                }
                              : null;

                            setSelectedUser(selected);
                            formik.setFieldValue(
                              "users",
                              selected?.value || "",
                            );
                          }}
                          placeholder="Select User"

                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isCallStageActive ? (
                <div className="stage-filter-row">
                  <div className="stage-filter-controls">
                    <div className="filter-label">
                      <img
                        src={isDarkMode ? CallStageDarkIcon : CallStageIcon}
                        alt="Call Stage"
                        className="filter-label-icon"
                      />
                      <span>Call Stage</span>
                    </div>
                    <div
                      className="stage-filter-value-wrapper"
                      ref={stageDropdownAnchorRef}
                    >
                      <Button
                        event={toggleStageDropdown}
                        backgroundColor="var(--body__background, #ffffff)"
                        color="var(--header__color)"
                        border="1px solid var(--border__color)"
                        style={{
                          width: "100%",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          fontFamily: "inherit",
                          fontSize: "14px",
                        }}
                        hoverStyles={{
                          borderColor: "#6860ee",
                        }}
                        hoverd={true}
                        aria-label="Select stage"
                        aria-expanded={isStageDropdownOpen}
                      >
                        <span
                          style={{
                            flex: 1,
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {tempStageValue ? (
                            tempStageValue
                          ) : (
                            <span style={{ color: "#9AA0A6" }}>
                              Select stage
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            marginLeft: "8px",
                            flexShrink: 0,
                          }}
                        >
                          ▼
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
              {isIndustryActive ? (
                <div className="stage-filter-row">
                  <div className="stage-filter-controls">
                    <div className="filter-label">
                      <img
                        src={isDarkMode ? industryDarkIcon : industryIcon}
                        alt="Industry"
                        className="filter-label-icon"
                      />
                      <span>Industry</span>
                    </div>
                    <div className="stage-filter-value-wrapper">
                      <DynamicSelectInput
                        name="industry"
                        defaultOption="Select industry"
                        options={industryOptions}
                        value={formik.values.industry}
                        onChange={formik.handleChange}
                        wrapperStyle={{ width: "100%" }}
                        selectStyle={{
                          width: "100%",
                          height: "38px",
                          fontFamily: "inherit",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "var(--header__color)",
                          borderRadius: "4px",
                        }}
                        defaultOptionStyle={{ color: "#9AA0A6" }}
                        menuWidth={260}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {showDateFilter && (
                <div className="stage-filter-row">
                  <div className="stage-filter-controls">
                    <div className="filter-label">
                      <img
                        src={isDarkMode ? TimeDarkIcon : TimeIcon}
                        alt="Time"
                        className="filter-label-icon"
                      />
                      <span>Time</span>
                    </div>
                    <div className="date-filter-wrapper">
                      <DateFilter
                        value={tempDateValue}
                        onChange={handleDateSelect}
                        isOpen={isDateDropdownOpen}
                        onOpen={toggleDateDropdown}
                        onBlur={() => setIsDateDropdownOpen(false)}
                      />
                    </div>
                  </div>
                </div>
              )}
              {showDateRangeFilter && (
                <div className="stage-filter-row">
                  <div className="stage-filter-controls">
                    <div className="filter-label">
                      <img
                        src={isDarkMode ? TimeDarkIcon : TimeIcon}
                        alt="Date Range"
                        className="filter-label-icon"
                      />
                      <span>Date Range</span>
                    </div>
                    <div className="stage-date-range-wrapper">
                      <button
                        ref={fromDateRef}
                        type="button"
                        className="stage-date-range-control"
                        onClick={() => openDateRangePicker("from")}
                      >
                        <img
                          alt=""
                          src={isDarkMode ? darkCalendarIcon : calendarIcon}
                          className="stage-date-range-icon"
                        />
                        <span className={!tempFromDate ? "is-placeholder" : ""}>
                          {tempFromDate
                            ? formatDateRangeButtonLabel(tempFromDate)
                            : "From Date"}
                        </span>
                      </button>
                      <span className="stage-date-range-separator">-</span>
                      <button
                        ref={toDateRef}
                        type="button"
                        className="stage-date-range-control"
                        onClick={() => openDateRangePicker("to")}
                      >
                        <img
                          alt=""
                          src={isDarkMode ? darkCalendarIcon : calendarIcon}
                          className="stage-date-range-icon"
                        />
                        <span className={!tempToDate ? "is-placeholder" : ""}>
                          {tempToDate
                            ? formatDateRangeButtonLabel(tempToDate)
                            : "To Date"}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="stage-popup-actions">
              {showClearButton && (
                <Button
                  event={handleClear}
                  label="Clear"
                  aria-label="Clear filter changes"
                  backgroundColor="#a90606"
                  color="#ffffffff"
                  border="#a90606"
                  hoverd={false}
                />
              )}
              <Button
                event={handleApply}
                label="Apply"
                aria-label="Apply filters"
              />
            </div>
          </>
        )}
      </div>
      {openRangePicker && rangePickerPosition.top !== null && (
        <CustomDatePickerModal
          initialDate={
            openRangePicker === "from"
              ? tempFromDate || new Date()
              : tempToDate || new Date()
          }
          maxDate={new Date()}
          disablePast={false}
          onOk={(date) => {
            setRangePickerPosition({ top: null, left: null });
            if (openRangePicker === "from") {
              setTempFromDate(date);
            } else {
              setTempToDate(date);
            }
            setOpenRangePicker(null);
          }}
          onClose={() => {
            setOpenRangePicker(null);
            setRangePickerPosition({ top: null, left: null });
          }}
          top={rangePickerPosition.top}
          left={rangePickerPosition.left}
          zIndex={1300}
        />
      )}
    </>
  );

  const popupPortal = createPortal(popupContent, document.body);
  const dropdownPortal =
    isStageDropdownOpen && stageDropdownRect
      ? createPortal(
          <div
            className="stage-list-dropdown"
            ref={stageDropdownMenuRef}
            style={{
              top: `${stageDropdownRect.top}px`,
              left: `${stageDropdownRect.left}px`,
              width: `${stageDropdownRect.width}px`,
            }}
          >
            {stages && stages.length > 0 ? (
              stages.map((stage) => {
                const color =
                  (getStageColor && getStageColor(stage)) || "#6B7280";
                const isSelected = tempStageValue === stage;
                return (
                  <div
                    key={stage}
                    role="option"
                    tabIndex={0}
                    className={`stage-list-dropdown-item ${
                      isSelected ? "stage-list-dropdown-item--selected" : ""
                    }`}
                    onClick={() => handleStageSelect(stage)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleStageSelect(stage);
                      }
                    }}
                    aria-selected={isSelected}
                  >
                    <span
                      className="stage-dot"
                      style={{ background: color }}
                      aria-hidden="true"
                    />
                    <span className="stage-label">{stage}</span>
                  </div>
                );
              })
            ) : (
              <div className="stage-list-dropdown-empty">
                No stages available
              </div>
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {popupPortal}
      {dropdownPortal}
    </>
  );
};

StageFilterPopup.propTypes = {
  isOpen: PropTypes.bool,
  initialFilters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  initialSelected: PropTypes.arrayOf(PropTypes.string),
  stages: PropTypes.arrayOf(PropTypes.string),
  onApply: PropTypes.func,
  onApplyFull: PropTypes.func,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  setIsAllCandidatesActive: PropTypes.func,
  isTrackedByMeActive: PropTypes.bool,
  setIsTrackedByMeActive: PropTypes.func,
  isCreatedByMeActive: PropTypes.bool,
  setIsCreatedByMeActive: PropTypes.func,
  setTablePage: PropTypes.func,
  setSelectedCandidates: PropTypes.func,
  setIsSelectMode: PropTypes.func,
  setTrackedViewTab: PropTypes.func,
  isInitialLoading: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  isDarkMode: PropTypes.bool,
  subtitle: PropTypes.string,
  customContent: PropTypes.node,
  customActions: PropTypes.node,
  showCandidateViewSelection: PropTypes.bool,
  showDateFilter: PropTypes.bool,
  isIndustryActive: PropTypes.bool,
  includeUserFilterWithoutMode: PropTypes.bool,
  defaultCandidateViewOnOpen: PropTypes.oneOf(["trackedByMe"]),
  showDateRangeFilter: PropTypes.bool,
  fromDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  toDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  popupClassName: PropTypes.string,
  popupStyle: PropTypes.object,
};

export default StageFilterPopup;